import React, { useState, useRef, useEffect } from "react";
import { v4 as uuidv4 } from "uuid";
import {
  VerifyAdharOTPServices,
  SendAdharOTPServices,
} from "../../services/index";
import { useDispatch, useSelector } from "react-redux";
import { setPatientDetails } from "../../redux/slices/abhaSlice";
import { toast } from "react-toastify";
import { timerCount } from "../../config";

/* ── Step marker images ── */
import step1Img from "../../assets/image/Group 110901.png";
import step2Img from "../../assets/image/Group 110902.png";
import step3Img from "../../assets/image/Group 110919.png";
import formBgImg from "../../assets/image/Group 2147206200.png";
import about from "../../assets/image/about_nha_pg_logo.png";

const STEPS = [
  {
    img: step1Img,
    title: "Verify Aadhaar Details",
    desc: "Enter your Aadhaar number and verify with OTP received on Aadhaar linked mobile number.",
  },
  {
    img: step2Img,
    title: "Link mobile number with ABHA",
    desc: "Enter your mobile number and verify with OTP to link with ABHA Address.",
  },
  {
    img: step3Img,
    title: "Create your ABHA",
    desc: "Create your ABHA Number with a unique ABHA address.",
  },
];

const normalizeMobileNumber = (value) =>
  String(value || "").replace(/\D/g, "").slice(-10);

const getAadhaarVerifiedMobile = (result) =>
  result?.mobile ||
  result?.mobileNumber ||
  result?.mobileNo ||
  result?.phoneNumber ||
  result?.phone ||
  result?.aadhaarMobile ||
  result?.aadhaarMobileNumber;

const VerifyAdhaOtp = ({
  setAdharOtp,
  setMobileverifyOtp,
  setAdharNumber,
  setIsConfirm1,
  setCurrentStep,
}) => {
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [timer, setTimer] = useState(timerCount);
  const [canResend, setCanResend] = useState(false);
  const [resendCount, setResendCount] = useState(2);
  const [disableResend, setDisableResend] = useState(false);

  const inputRefs = useRef([]);
  const dispatch = useDispatch();
  const mobilemsg = useSelector((state) => state.user.msgAdharmobile);
  const maskedNumber = mobilemsg?.match(/\*{6}\d{4}/)?.[0];

  useEffect(() => {
    let interval = null;
    if (timer > 0) {
      setCanResend(false);
      interval = setInterval(() => {
        setTimer((prev) => prev - 1);
      }, 1000);
    } else {
      setCanResend(true);
      clearInterval(interval);
    }
    return () => clearInterval(interval);
  }, [timer]);

  const handleInputChange = (e, index) => {
    const value = e.target.value.replace(/\D/g, "");
    if (value.length <= 1) {
      const newOtp = [...otp];
      newOtp[index] = value;
      setOtp(newOtp);
      if (value && index < 5) inputRefs.current[index + 1].focus();
    }
  };

  const handleKeyDown = (e, index) => {
    if (e.key === "Backspace" && otp[index] === "" && index > 0) {
      inputRefs.current[index - 1].focus();
    }
  };

  const handleContinue = async (e) => {
    e.preventDefault();
    const otpValue = otp.join("");
    const mobile=localStorage.getItem("mobile");
    console.log(mobile,'mobile from aadahr');
    
    try {
      const response = await VerifyAdharOTPServices({
        body: {
          referenceId: uuidv4(),
          otpValue,
          txnId: localStorage.getItem("txnId"),
          mobile,
        },
      });

      if (response.status === true && response.result.txnId) {
        dispatch(setPatientDetails(response.result));
        const aadhaarVerifiedMobile = normalizeMobileNumber(
          getAadhaarVerifiedMobile(response.result)
        );
        if (aadhaarVerifiedMobile) {
          localStorage.setItem("aadhaarVerifiedMobile", aadhaarVerifiedMobile);
        }
        if (response.result.abhaAddress) {
          localStorage.setItem("preferredAbhaAddress", response.result.abhaAddress);
        }
        if (response.result.abhaNumber) {
          localStorage.setItem("preferredAbhaNumber", response.result.abhaNumber);
        }
        if (response.result.xToken) {
          localStorage.setItem("abhaxtoken", response.result.xToken);
        }
        if (response.result) {
          localStorage.setItem("aadhaarVerfiUser", JSON.stringify(response.result));
        }
        setCurrentStep("MobileVerificationOTPSend");
      } else {
        let errorMessageRes = "";
        if (response.result?.error?.message.includes(":")) {
          errorMessageRes = response?.result?.error?.message?.split(":")[2];
        }
        const errorMessage =
          errorMessageRes ||
          response?.result?.error?.message ||
          response?.message ||
          "OTP verification failed. Please try again.";
        toast.error(errorMessage);
      }
    } catch (err) {
      console.error("Verification failed", err);
    }
  };

  const handleResendOtp = async () => {
    if (!canResend) return;
    if (resendCount <= 0 || disableResend) {
      setDisableResend(true);
      toast.error("You have reached the maximum resend attempts.");
      return;
    }
    try {
      const aadhaarNumber = localStorage.getItem("aadhaarNumber");
      const body = { aadhaarNumber, resent_count: resendCount };
      const response = await SendAdharOTPServices({ body });
      if (response.status) {
        localStorage.setItem("txnId", response.result.txnId);
        setOtp(["", "", "", "", "", ""]);
        if (inputRefs.current[0]) inputRefs.current[0].focus();
        setResendCount((prev) => {
          const newCount = prev - 1;
          if (newCount <= 0) {
            setDisableResend(true);
            toast.error("You have reached the maximum resend attempts.");
          } else {
            toast.success("OTP resent successfully!");
          }
          return newCount;
        });
        setTimer(timerCount);
      } else {
        toast.error(
          response.data.error.message || response?.message || "Failed to resend OTP."
        );
      }
    } catch (err) {
      console.error("Resend OTP failed", err);
      toast.error("Failed to resend OTP. Please try again.");
    }
  };

  return (
    <section className="lmop-section">
      <div className="container">
        <h2 className="lmop-left-title">Create ABHA Number in 3 Steps</h2>
        <p className="lmop-left-desc">
          Create ABHA and store all your medical records in one place.
          Take part in the{" "}
          <span className="highlight">Ayushman Bharat Digital Mission (ABDM)</span>{" "}
          and get started on your digital health journey.
        </p>

        <div className="row align-items-start g-4 g-lg-5">

          {/* ── LEFT: 3 Steps ── */}
          <div className="col-12 col-md-6">
            <div className="lmop-steps">
              {STEPS.map((step, idx) => (
                <React.Fragment key={idx}>
                  <div className="lmop-step-item">
                    <div className="lmop-step-marker">
                      <img src={step.img} alt={`Step ${idx + 1}`} />
                    </div>
                    <div className="lmop-step-body">
                      <h4>{step.title}</h4>
                      <p>{step.desc}</p>
                    </div>
                  </div>
                  {idx < STEPS.length - 1 && <div className="lmop-step-line" />}
                </React.Fragment>
              ))}
            </div>
          </div>

          {/* ── RIGHT: OTP Form Card ── */}
          <div className="col-12 col-md-6">
            <div
              className="lmop-card-wrap"
              style={{
                backgroundImage: `url(${formBgImg})`,
                backgroundSize: "cover",
                backgroundPosition: "center",
              }}
            >
              <div className="lmop-card-inner">
                <h3 className="lmop-card-title">Verify Your Aadhaar</h3>
                <p className="lmop-card-subtitle">
                  We have sent the OTP to the mobile number ending in {maskedNumber}.
                </p>

                <form onSubmit={handleContinue}>
                  {/* OTP Boxes */}
                  <div style={{ display: "flex", gap: "10px", justifyContent: "center", marginBottom: "18px" }}>
                    {otp.map((digit, index) => (
                      <input
                        key={index}
                        type="text"
                        maxLength="1"
                        value={digit}
                        onChange={(e) => handleInputChange(e, index)}
                        onKeyDown={(e) => handleKeyDown(e, index)}
                        ref={(el) => (inputRefs.current[index] = el)}
                        autoComplete="off"
                        style={{
                          width: "44px",
                          height: "50px",
                          textAlign: "center",
                          fontSize: "1.2rem",
                          fontWeight: "700",
                          border: "1.5px solid #e2e5ec",
                          borderRadius: "10px",
                          background: "#f5f6f8",
                          outline: "none",
                          fontFamily: "Manrope, sans-serif",
                          color: "#222",
                        }}
                      />
                    ))}
                  </div>

                  <button
                    type="submit"
                    className={`lmop-continue-btn ${otp.join("").length !== 6 ? "bg-disable" : ""}`}
                    disabled={otp.join("").length !== 6}
                  >
                    Continue to Mobile Verification
                  </button>

                  <p style={{ textAlign: "center", marginTop: "14px", fontSize: "0.85rem", color: "#555" }}>
                    Didn't receive OTP?{" "}
                    {!disableResend && !canResend ? (
                      <span style={{ color: "#1a7fff", fontWeight: "600" }}>Resend in {timer}s</span>
                    ) : (
                      <button
                        type="button"
                        onClick={handleResendOtp}
                        disabled={disableResend}
                        style={{
                          background: "none",
                          border: "none",
                          color: disableResend ? "#aaa" : "#1a7fff",
                          fontWeight: "600",
                          cursor: disableResend ? "not-allowed" : "pointer",
                          fontFamily: "Manrope, sans-serif",
                          fontSize: "0.85rem",
                          padding: 0,
                        }}
                      >
                        Resend OTP
                      </button>
                    )}
                  </p>

                  {!disableResend && resendCount > 0 && (
                    <p style={{ textAlign: "center", fontSize: "0.78rem", color: "#888" }}>
                      You have {resendCount} resend attempt{resendCount > 1 ? "s" : ""} left.
                    </p>
                  )}
                  {disableResend && (
                    <p style={{ textAlign: "center", fontSize: "0.78rem", color: "#cf1f1f" }}>
                      You have no resend attempts left.
                    </p>
                  )}
                </form>

                <div className="lmop-nha-row">
                  <span className="lmop-nha-label">Approved by NHA</span>
                  <img src={about} alt="NHA Logo" className="lmop-nha-logo" />
                </div>

              </div>
            </div>
          </div>

        </div>
      </div>
    </section>
  );
};

export default VerifyAdhaOtp;
